import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { useParams } from 'react-router-dom';
import useMockLoading from '../../../../components/Hooks/useMockLoading';
import { useBlockRefreshIf } from '../../../../hooks/useBlockRefreshIf';
import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { WithId } from '../../../../types/Base.types';
import { retrieveOrderProductFromUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { useTranslation } from '../../../translation/TranslationContext';
import { useOrderProductsContext } from '../../contexts/OrderProducts.context';
import { BatchDeleteOrderProductsDto } from '../../products/dtos/batch-delete-order-products.dto';
import { BatchOrderProduct } from '../../products/interfaces/order-products-repository.types';
import { orderProductsRepository } from '../../products/orderProducts.repository';
import { RemoveOrderProductsFromOrderModalProps } from './RemoveOrderProductsFromOrderModal.interfaces';

const RemoveOrderProductsFromOrderModal = ({
  isOpen,
  selectedItems,
  isAllItemsSelected,
  onClose,
}: RemoveOrderProductsFromOrderModalProps) => {
  const { translate } = useTranslation();
  const { id: orderId } = useParams<WithId>();
  const { handleMessage } = snackbarStore;
  const { onRemoveProducts } = useOrderProductsContext();
  const repository = orderProductsRepository(orderId);

  const handleSubmit = async () => {
    const orderProducts: BatchOrderProduct[] = Array.from(selectedItems)
      .map((item) => retrieveOrderProductFromUniqueIdentifier(item))
      .filter((item) => item !== null);
    const data: BatchDeleteOrderProductsDto = {
      filter: {
        orderProducts: isAllItemsSelected ? undefined : orderProducts,
        allOrderProducts: isAllItemsSelected,
      },
    };
    const response = await repository.batchDelete(data);
    if (!response.ok) {
      handleMessage({
        type: 'error',
        message: translate(LangEnum.REMOVE_FROM_ORDER_ERROR_MESSAGE),
      });
    } else {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.REMOVE_FROM_ORDER_SUCCESS_MESSAGE),
      });
      await onRemoveProducts(orderProducts);
      onClose(true);
    }
  };

  const { isLoading, handleAsyncFunction } = useMockLoading({ asyncFunction: handleSubmit });

  useBlockRefreshIf({ condition: isLoading });

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{ title: LangEnum.REMOVE_ORDER_PRODUCTS_FROM_ORDER_ALERT }}
      onClose={handleClose}
      footer={{
        primaryAction: {
          label: isLoading ? LangEnum.GLOBAL_REMOVING_LABEL : LangEnum.GLOBAL_REMOVE_LABEL,
          isLoading,
          onClick: handleAsyncFunction,
        },
        secondaryAction: { label: LangEnum.GLOBAL_CANCEL_LABEL, onClick: handleClose },
      }}
    >
      <Typography color="text.secondary">
        {translate(LangEnum.REMOVE_ORDER_PRODUCTS_FROM_ORDER_DESCRIPTION)}
      </Typography>
    </Modal>
  );
};

export default RemoveOrderProductsFromOrderModal;
